/**
 * CL-DRAW-ORDER-001A — Canonical project drawing inventory (pure).
 *
 * Invariant: every client Drawings & review file id must be admin-manageable
 * for the same project. Admin may additionally include unshared drawings.
 *
 * Identity: pmProjectFiles document id (stable across shares/revisions metadata).
 * Separate file docs are never deduped by sheet number or display name alone.
 */

import { isDrawingReviewDomainFile } from "./clientDrawingReviewGroups";
import { isControlledProjectDocument } from "./controlledProjectDocuments";
import { isClientProjectDocument } from "./clientProjectDocuments";
import { classifyProjectFileUpload } from "./projectFileUploadClassificationModel";
import { sortDrawingsCanonical } from "./drawingOrderModel";
import { isCurrentDrawingLibraryRecord } from "./drawingOrganizationModel";
import {
  individualDrawingsInAdminOrder,
  resolveOverallPackageState,
} from "./drawingDocumentRoleModel";

function safeStr(value) {
  try {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  } catch {
    return "";
  }
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

/** Stable canonical identity for order arrays and inventory maps. */
export function canonicalDrawingIdentity(fileOrId) {
  if (fileOrId && typeof fileOrId === "object") return safeStr(fileOrId.id || fileOrId.fileId);
  return safeStr(fileOrId);
}

export function isDeletedDrawingInventoryFile(file) {
  const status = lower(file?.status || "active");
  if (status === "deleted") return true;
  if (file?.deleted === true) return true;
  return false;
}

function classifiesAsDrawing(file) {
  const stored = lower(file?.libraryType);
  if (stored === "drawing") return true;
  if (lower(file?.manualLibraryType || file?.libraryTypeManual) === "drawing") return true;
  try {
    const classified = classifyProjectFileUpload({
      fileName: file?.name,
      mimeType: file?.contentType || file?.type,
      selectedLibraryType: file?.manualLibraryType || file?.libraryTypeManual || "auto",
      uploadContext: [file?.category, file?.subcategory, file?.deliverableType].filter(Boolean).join(" "),
      metadata: file,
    });
    return lower(classified?.libraryType) === "drawing";
  } catch {
    return false;
  }
}

/**
 * Same domain gate the client portal uses for Drawings & review membership,
 * excluding controlled / client-supporting documents.
 */
export function isClientVisibleDrawingInventoryFile(file) {
  if (!file || isDeletedDrawingInventoryFile(file)) return false;
  if (isControlledProjectDocument(file)) return false;
  if (isClientProjectDocument(file)) return false;
  return isDrawingReviewDomainFile(file);
}

/**
 * Admin-manageable drawings: every client-visible drawing plus admin drawing sheets
 * (including ones not yet shared). Does not hide by enrichment-only reclassification —
 * stored libraryType drawing and client drawing-domain membership both qualify.
 */
export function isAdminManageableDrawingInventoryFile(file) {
  if (!file || isDeletedDrawingInventoryFile(file)) return false;
  if (isControlledProjectDocument(file)) return false;
  if (isClientProjectDocument(file)) return false;
  if (isDrawingReviewDomainFile(file)) return true;
  if (classifiesAsDrawing(file)) return true;
  return false;
}

function dedupeByCanonicalId(files) {
  const out = [];
  const seen = new Set();
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (!file) return;
    const id = canonicalDrawingIdentity(file);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(file);
  });
  return out;
}

export function buildClientVisibleDrawingInventory(projectFiles = []) {
  return dedupeByCanonicalId(
    (Array.isArray(projectFiles) ? projectFiles : []).filter(isClientVisibleDrawingInventoryFile)
  );
}

export function buildAdminManageableDrawingInventory(projectFiles = []) {
  return dedupeByCanonicalId(
    (Array.isArray(projectFiles) ? projectFiles : []).filter(isAdminManageableDrawingInventoryFile)
  );
}

export function drawingInventoryIds(files = []) {
  return (Array.isArray(files) ? files : [])
    .map((f) => canonicalDrawingIdentity(f))
    .filter(Boolean);
}

/**
 * @returns {{ ok: boolean, missingIds: string[], clientIds: string[], adminIds: string[] }}
 */
export function assertClientDrawingInventorySubsetOfAdmin(clientFiles, adminFiles) {
  const clientIds = drawingInventoryIds(clientFiles);
  const adminSet = new Set(drawingInventoryIds(adminFiles));
  const missingIds = clientIds.filter((id) => !adminSet.has(id));
  return {
    ok: missingIds.length === 0,
    missingIds,
    clientIds,
    adminIds: [...adminSet],
  };
}

/**
 * Ordered admin Drawings-tab / Order drawings modal view model.
 */
export function buildAdminDrawingsTabViewModel({
  projectFiles = [],
  project = null,
} = {}) {
  const inventory = buildAdminManageableDrawingInventory(projectFiles);
  const orderScope = inventory.filter((f) => isCurrentDrawingLibraryRecord(f));
  const orderedAll = sortDrawingsCanonical(inventory, project, { includeExcluded: true });
  const packageState = resolveOverallPackageState(inventory);
  const ordered = individualDrawingsInAdminOrder(orderScope, project);
  const ids = drawingInventoryIds(ordered);
  return {
    inventory,
    orderedAll,
    overallPackage: packageState.overallPackage,
    packageConflict: packageState.packageConflict,
    conflictingPackages: packageState.conflictingPackages,
    ordered,
    ids,
    count: ordered.length,
    orderButtonLabel: `Order drawings (${ordered.length})`,
  };
}

export function buildClientDrawingsListViewModel({
  projectFiles = [],
  project = null,
  listAudience = "client",
  viewerContext = null,
  groupFn = null,
} = {}) {
  const inventory = buildClientVisibleDrawingInventory(projectFiles);
  const ordered = sortDrawingsCanonical(inventory, project, { includeExcluded: true });
  const grouped =
    typeof groupFn === "function"
      ? groupFn(ordered, listAudience, viewerContext, project)
      : { all: ordered, needsReview: [], reviewedInProgress: [], reviewedApproved: ordered };
  return {
    inventory,
    ordered,
    ids: drawingInventoryIds(ordered),
    count: ordered.length,
    grouped,
  };
}
